import { Link, useNavigate } from 'react-router-dom';
import {
    ShoppingCart,
    User,
    Search,
    Menu,
    LogOut,
    Package,
    Settings,
    Phone,
    MapPin,
    X,
} from 'lucide-react';
import { useAuthStore, useCartStore } from '@/store';
import { ROUTES } from '@/config';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { Badge } from '@/shared/components/ui/badge';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useFlyAnimation } from '@/shared/components/common/FlyAnimationProvider';
import { useCategories } from '@/shared/hooks/useCategories';
import { usePublicSettings } from '@/shared/hooks/useSettings';

const Header: React.FC = () => {
    const navigate = useNavigate();
    const { user, isAuthenticated, logout } = useAuthStore();
    const { totalItems } = useCartStore();
    const [searchQuery, setSearchQuery] = useState('');
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [cartBump, setCartBump] = useState(false);
    const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);
    const { cartIconRef } = useFlyAnimation();
    const { data: categories = [], isLoading: categoriesLoading } = useCategories();
    const { data: settings } = usePublicSettings();

    // Get data from settings
    const logoUrl = settings?.data?.general?.logo?.url;
    const siteName = settings?.data?.general?.siteName || 'Cosmetic Store';
    const contactPhone = settings?.data?.general?.contactPhone;
    const address = settings?.data?.general?.address;
    const freeShippingThreshold = settings?.data?.shipping?.methods?.standard?.freeShippingThreshold;

    // Trigger bump animation when totalItems changes
    useEffect(() => {
        if (totalItems > 0) {
            setCartBump(true);
            const timer = setTimeout(() => setCartBump(false), 600);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [totalItems]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`${ROUTES.PRODUCTS}?search=${encodeURIComponent(searchQuery)}`);
        }
    };

    const handleLogout = () => {
        logout();
        navigate(ROUTES.HOME);
    };

    return (
        <header className="sticky top-0 z-50 bg-white shadow-sm">
            {/* Top bar */}
            <div className="bg-primary text-white text-xs py-2">
                <div className="container flex items-center justify-between">
                    <div className="hidden md:flex items-center gap-6">
                        {contactPhone && (
                            <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3" />
                                Hotline: {contactPhone}
                            </span>
                        )}
                        {address && (
                            <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {address}
                            </span>
                        )}
                    </div>
                    <div className="text-center md:text-right flex-1 md:flex-none">
                        {freeShippingThreshold ? (
                            <>
                                🎁 <span className="font-medium">Freeship</span> đơn từ{' '}
                                {(freeShippingThreshold / 1000).toFixed(0)}K
                            </>
                        ) : (
                            <>
                                🎁 <span className="font-medium">Freeship</span> đơn từ 299K
                            </>
                        )}{' '}
                        | Giao nhanh trong Hà Nội
                    </div>
                </div>
            </div>

            {/* Main header */}
            <div className="container py-3">
                <div className="flex items-center gap-4">
                    {/* Mobile menu button */}
                    <Button
                        variant="ghost"
                        size="icon"
                        className="md:hidden"
                        onClick={() => setMobileMenuOpen(true)}
                    >
                        <Menu className="h-5 w-5" />
                    </Button>

                    {/* Logo */}
                    <Link to={ROUTES.HOME} className="flex items-center gap-2 flex-shrink-0">
                        {logoUrl ? (
                            <img
                                src={logoUrl}
                                alt={siteName}
                                className="h-10 md:h-12 w-auto object-contain"
                            />
                        ) : (
                            <span className="text-xl md:text-2xl font-display font-bold text-primary">
                                {siteName}
                            </span>
                        )}
                    </Link>

                    {/* Search */}
                    <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-2xl mx-4">
                        <div className="relative w-full">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Tìm kiếm sản phẩm, thương hiệu..."
                                className="pl-10 pr-20 h-10 rounded-full border-primary/30 focus:border-primary"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <Button
                                type="submit"
                                size="sm"
                                className="absolute right-1 top-1/2 -translate-y-1/2 rounded-full h-8"
                            >
                                Tìm kiếm
                            </Button>
                        </div>
                    </form>

                    {/* Actions */}
                    <div className="flex items-center gap-1 md:gap-2 ml-auto">
                        {/* Cart */}
                        <motion.div
                            animate={cartBump ? { scale: [1, 1.2, 1], rotate: [0, -10, 10, -10, 10, 0] } : {}}
                            transition={{ duration: 0.6, ease: 'easeInOut' }}
                        >
                            <Button
                                ref={cartIconRef}
                                data-cart-icon
                                variant="ghost"
                                size="icon"
                                className="relative"
                                onClick={() => navigate(ROUTES.CART)}
                            >
                                <ShoppingCart className="h-5 w-5" />
                                {totalItems > 0 && (
                                    <motion.div
                                        animate={cartBump ? { scale: [1, 1.3, 1] } : {}}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs">
                                            {totalItems > 99 ? '99+' : totalItems}
                                        </Badge>
                                    </motion.div>
                                )}
                            </Button>
                        </motion.div>

                        {/* User menu */}
                        {isAuthenticated ? (
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" className="hidden md:flex items-center gap-2">
                                        <Avatar className="h-8 w-8">
                                            <AvatarImage src={user?.avatar} alt={user?.name} />
                                            <AvatarFallback className="bg-primary text-white">
                                                {user?.name?.charAt(0).toUpperCase()}
                                            </AvatarFallback>
                                        </Avatar>
                                        <span className="text-sm font-medium max-w-[100px] truncate">
                                            {user?.name}
                                        </span>
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <DropdownMenuLabel>Tài khoản của tôi</DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => navigate(ROUTES.PROFILE)}>
                                        <Settings className="mr-2 h-4 w-4" />
                                        Thông tin cá nhân
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => navigate(ROUTES.ORDERS)}>
                                        <Package className="mr-2 h-4 w-4" />
                                        Đơn hàng của tôi
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                                        <LogOut className="mr-2 h-4 w-4" />
                                        Đăng xuất
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <Button
                                variant="outline"
                                size="sm"
                                className="hidden md:flex"
                                onClick={() => navigate(ROUTES.LOGIN)}
                            >
                                <User className="mr-2 h-4 w-4" />
                                Đăng nhập
                            </Button>
                        )}
                    </div>
                </div>

                {/* Mobile search */}
                <form onSubmit={handleSearch} className="mt-3 md:hidden">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Tìm kiếm sản phẩm..."
                            className="pl-10 h-10 rounded-full"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </form>
            </div>

            {/* Navigation - Desktop */}
            <nav className="border-t bg-gray-50 hidden md:block">
                <div className="container">
                    <ul className="flex items-center gap-0">
                        {categoriesLoading ? (
                            <li className="px-4 py-3 text-sm text-muted-foreground">Đang tải...</li>
                        ) : (
                            categories.slice(0, 6).map((category) => (
                                <li
                                    key={category._id}
                                    className="relative group"
                                    onMouseEnter={() => setHoveredCategory(category._id)}
                                    onMouseLeave={() => setHoveredCategory(null)}
                                >
                                    <Link
                                        to={`/products?category=${category.slug}`}
                                        className="block px-4 py-3 text-sm font-medium text-gray-700 hover:text-primary hover:bg-primary/5 transition-colors"
                                    >
                                        {category.name}
                                        {category.subcategories && category.subcategories.length > 0 && (
                                            <span className="ml-1 text-xs">▼</span>
                                        )}
                                    </Link>

                                    {/* Subcategories Dropdown */}
                                    {category.subcategories &&
                                        category.subcategories.length > 0 &&
                                        hoveredCategory === category._id && (
                                            <div className="absolute top-full left-0 w-56 bg-white shadow-lg border rounded-md z-50 py-2">
                                                <ul className="space-y-1">
                                                    {category.subcategories.map((subcategory) => (
                                                        <li key={subcategory._id}>
                                                            <Link
                                                                to={`/products?category=${subcategory.slug}`}
                                                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-primary/5 hover:text-primary transition-colors"
                                                                onClick={() => setHoveredCategory(null)}
                                                            >
                                                                {subcategory.name}
                                                            </Link>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                </li>
                            ))
                        )}
                        <li>
                            <Link
                                to="/pages/ve-chung-toi"
                                className="block px-4 py-3 text-sm font-medium text-gray-700 hover:text-primary hover:bg-primary/5 transition-colors"
                            >
                                Về chúng tôi
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/contact"
                                className="block px-4 py-3 text-sm font-medium text-gray-700 hover:text-primary hover:bg-primary/5 transition-colors"
                            >
                                Liên hệ
                            </Link>
                        </li>
                    </ul>
                </div>
            </nav>

            {/* Mobile Menu Drawer */}
            {mobileMenuOpen && (
                <div className="fixed inset-0 z-50 md:hidden">
                    <div
                        className="absolute inset-0 bg-black/50"
                        onClick={() => setMobileMenuOpen(false)}
                    />
                    <div className="absolute left-0 top-0 h-full w-72 bg-white shadow-xl">
                        <div className="flex items-center justify-between p-4 border-b">
                            <span className="font-bold text-primary">Menu</span>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                <X className="h-5 w-5" />
                            </Button>
                        </div>
                        <div className="py-2 max-h-[calc(100vh-200px)] overflow-y-auto">
                            {categoriesLoading ? (
                                <div className="px-4 py-3 text-sm text-muted-foreground">Đang tải...</div>
                            ) : (
                                categories.slice(0, 6).map((category) => (
                                    <div key={category._id}>
                                        <Link
                                            to={`/products?category=${category.slug}`}
                                            className="block px-4 py-3 text-sm hover:bg-gray-100"
                                            onClick={() => setMobileMenuOpen(false)}
                                        >
                                            {category.name}
                                        </Link>
                                        {category.subcategories && category.subcategories.length > 0 && (
                                            <div className="pl-6 space-y-1">
                                                {category.subcategories.map((subcategory) => (
                                                    <Link
                                                        key={subcategory._id}
                                                        to={`/products?category=${subcategory.slug}`}
                                                        className="block px-4 py-2 text-sm text-muted-foreground hover:bg-gray-100"
                                                        onClick={() => setMobileMenuOpen(false)}
                                                    >
                                                        {subcategory.name}
                                                    </Link>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                            <Link
                                to="/pages/ve-chung-toi"
                                className="block px-4 py-3 text-sm hover:bg-gray-100"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                Về chúng tôi
                            </Link>
                            <Link
                                to="/contact"
                                className="block px-4 py-3 text-sm hover:bg-gray-100"
                                onClick={() => setMobileMenuOpen(false)}
                            >
                                Liên hệ
                            </Link>
                        </div>
                        <div className="border-t p-4">
                            {isAuthenticated ? (
                                <div className="space-y-2">
                                    <Link
                                        to={ROUTES.PROFILE}
                                        className="block px-4 py-2 text-sm hover:bg-gray-100 rounded"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Tài khoản
                                    </Link>
                                    <Link
                                        to={ROUTES.ORDERS}
                                        className="block px-4 py-2 text-sm hover:bg-gray-100 rounded"
                                        onClick={() => setMobileMenuOpen(false)}
                                    >
                                        Đơn hàng
                                    </Link>
                                    <button
                                        onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                                        className="block w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-50 rounded"
                                    >
                                        Đăng xuất
                                    </button>
                                </div>
                            ) : (
                                <Button
                                    className="w-full"
                                    onClick={() => { navigate(ROUTES.LOGIN); setMobileMenuOpen(false); }}
                                >
                                    Đăng nhập
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;
